import { useCallback, useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import qs from 'qs';
import { post } from '../utils/apiUtilities';
import $ from 'jquery';
import rr from '../../scripts/radresponder';
import toastr from 'toastr';

function SetupAuthenticator({ location, setIsAuthenticated }) {
    const queryString = qs.parse(location.search, { ignoreQueryPrefix: true });
    const returnUrl = queryString.ReturnUrl || queryString.returnUrl;
    const token = queryString.token;
    const username = queryString.username;

    const [code, setCode] = useState('');
    const [secretKey, setSecretKey] = useState('');
    const [qrCodeBase64, setQrCodeBase64] = useState('');
    const [manualEntryKey, setManualEntryKey] = useState('');
    const [rememberDevice, setRememberDevice] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [showManualEntry, setShowManualEntry] = useState(false);
    const [alreadySetup, setAlreadySetup] = useState(false);

    const loadSetupData = useCallback(async () => {
        try {
            setIsLoading(true);
            const data = await $.get(rr.getApiAddress() + "account/setupauthenticator/" + token);

            if (data.alreadySetup) {
                setAlreadySetup(true);
                // Redirect to verify page if already set up
                window.location.href = `/app/account/multifactor/verifyauthenticator?token=${token}&returnUrl=${encodeURIComponent(returnUrl)}&username=${encodeURIComponent(username)}`;
                return;
            }

            setSecretKey(data.secretKey);
            setQrCodeBase64(data.qrCodeBase64);
            setManualEntryKey(data.manualEntryKey || data.formattedSecretKey);
        }
        catch (err) {
            toastr.error(rr.extractError(err, err, err));
            setErrorMessage('Failed to load authenticator setup. Please try again.');
        }
        finally {
            setIsLoading(false);
        }
    }, [token, returnUrl, username]);

    useEffect(() => {
        loadSetupData();
    }, [loadSetupData]);

    const verify = async (e) => {
        e.preventDefault();
        setIsVerifying(true);
        setErrorMessage('');

        try {
            const d = await post('account/enableauthenticator', {
                identifier: token,
                code: code,
                secretKey: secretKey,
                rememberDevice: rememberDevice,
                returnUrl: returnUrl,
                isSetup: true
            });

            setIsAuthenticated(true);
            toastr.success(d.message || 'Authenticator successfully configured!');
            window.location.href = d.redirect;
        }
        catch (err) {
            setIsVerifying(false);
            if (err && err.responseText) {
                try {
                    const j = JSON.parse(err.responseText);
                    setErrorMessage(j.detail || j.message || err.responseText);
                }
                catch {
                    setErrorMessage((err.status === 400 || err.status === 401) ? err.responseText : 'An error occurred while verifying. Please try again.');
                }
            }
        }
        finally {
            setIsVerifying(false);
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(secretKey).then(() => {
            toastr.success('Secret key copied to clipboard!');
        }).catch(() => {
            toastr.error('Failed to copy to clipboard');
        });
    };

    if (isLoading) {
        return (
            <>
                <Helmet>
                    <title>Setup Authenticator | CBRN Network</title>
                </Helmet>
                <div id="layout-shell" className="shell top100">
                    <div className="main-content container" id="loginCont" tabIndex="-1">
                        <div className="row">
                            <main>
                                <div className="col-sm-12 col-md-8 col-lg-6 col-md-offset-2 col-lg-offset-3">
                                    <div className="panel panel-default">
                                        <div className="panel-body text-center">
                                            <i className="fa fa-spinner fa-spin fa-2x"></i>
                                            <p>Loading authenticator setup...</p>
                                        </div>
                                    </div>
                                </div>
                            </main>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (alreadySetup) {
        return (
            <>
                <Helmet>
                    <title>Setup Authenticator | CBRN Network</title>
                </Helmet>
                <div id="layout-shell" className="shell top100">
                    <div className="main-content container" id="loginCont" tabIndex="-1">
                        <div className="row">
                            <main>
                                <div className="col-sm-12 col-md-8 col-lg-6 col-md-offset-2 col-lg-offset-3">
                                    <div className="panel panel-default">
                                        <div className="panel-body text-center">
                                            <p>Redirecting to verification...</p>
                                        </div>
                                    </div>
                                </div>
                            </main>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Helmet>
                <title>Setup Authenticator | CBRN Network</title>
            </Helmet>
            <div id="layout-shell" className="shell top100">
                <div className="main-content container" id="loginCont" tabIndex="-1">
                    <div className="row">
                        <main>
                            <div className="col-sm-12 col-md-8 col-lg-6 col-md-offset-2 col-lg-offset-3">
                                <form autoComplete="off" className="form-horizontal signin-form" onSubmit={verify}>
                                    {errorMessage && (
                                        <div className="alert alert-danger">
                                            <span dangerouslySetInnerHTML={{ __html: errorMessage }}></span>
                                        </div>
                                    )}

                                    <div className="panel panel-default">
                                        <div className="panel-heading">
                                            <h4><i className="fa fa-shield"></i> Setup Authenticator App</h4>
                                        </div>
                                        <div className="panel-body">
                                            <div className="bottom10 bigger-120">{username}</div>

                                            <div className="alert alert-info">
                                                <strong>Step 1:</strong> Download an authenticator app
                                                <ul className="list-unstyled" style={{ marginTop: '10px', marginLeft: '10px' }}>
                                                    <li><i className="fa fa-mobile"></i> Microsoft Authenticator</li>
                                                    <li><i className="fa fa-mobile"></i> Google Authenticator</li>
                                                    <li><i className="fa fa-mobile"></i> Authy</li>
                                                </ul>
                                            </div>

                                            <div className="form-group">
                                                <label className="col-xs-12">
                                                    <strong>Step 2:</strong> Scan this QR code with your authenticator app:
                                                </label>
                                                <div className="col-xs-12 text-center">
                                                    {qrCodeBase64 && (
                                                        <img
                                                            src={`data:image/png;base64,${qrCodeBase64}`}
                                                            alt="QR Code for Authenticator Setup"
                                                            style={{ maxWidth: '200px', border: '1px solid #ddd', padding: '10px', borderRadius: '4px' }}
                                                        />
                                                    )}
                                                </div>
                                            </div>

                                            <div className="form-group">
                                                <div className="col-xs-12">
                                                    <button
                                                        type="button"
                                                        className="btn btn-link btn-sm"
                                                        onClick={() => setShowManualEntry(!showManualEntry)}
                                                    >
                                                        <i className={`fa fa-${showManualEntry ? 'minus' : 'plus'}-square-o`}></i>
                                                        &nbsp; Can't scan? Enter code manually
                                                    </button>
                                                </div>
                                            </div>

                                            {showManualEntry && (
                                                <div className="form-group">
                                                    <div className="col-xs-12">
                                                        <div className="well" style={{ wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '14px' }}>
                                                            <strong>Secret Key:</strong><br />
                                                            {manualEntryKey}
                                                            <button
                                                                type="button"
                                                                className="btn btn-default btn-xs pull-right"
                                                                onClick={copyToClipboard}
                                                                title="Copy to clipboard"
                                                            >
                                                                <i className="fa fa-copy"></i>
                                                            </button>
                                                        </div>
                                                        <small className="text-muted">
                                                            Enter this key manually in your authenticator app if you cannot scan the QR code.
                                                        </small>
                                                    </div>
                                                </div>
                                            )}

                                            <hr />

                                            <div className="form-group">
                                                <label className="col-xs-12 required-field-indicator">
                                                    <strong>Step 3:</strong> Enter the 6-digit code from your authenticator app:
                                                </label>
                                                <div className="col-xs-12">
                                                    <input
                                                        className="form-control input-lg text-center"
                                                        type="text"
                                                        inputMode="numeric"
                                                        pattern="[0-9]*"
                                                        maxLength="6"
                                                        placeholder="000000"
                                                        value={code}
                                                        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                                        autoFocus
                                                        style={{ letterSpacing: '8px', fontSize: '24px' }}
                                                    />
                                                </div>
                                            </div>

                                            <div className="form-group">
                                                <div className="col-xs-12">
                                                    <label>
                                                        <input
                                                            type="checkbox"
                                                            checked={rememberDevice}
                                                            onChange={(e) => setRememberDevice(e.target.checked)}
                                                        />
                                                        &nbsp; Don't ask again for 30 days on this device
                                                    </label>
                                                </div>
                                            </div>

                                            <div className="form-group">
                                                <div className="col-xs-12">
                                                    <button
                                                        id="verifyAuthenticatorButton"
                                                        className="btn btn-success btn-lg btn-block"
                                                        name="button"
                                                        disabled={isVerifying || code.length !== 6}
                                                        type="submit"
                                                    >
                                                        {isVerifying ? (
                                                            <><i className="fa fa-spinner fa-spin"></i> Verifying...</>
                                                        ) : (
                                                            <><i className="fa fa-check"></i> Verify & Enable</>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="form-group">
                                                <div className="col-xs-12 text-center">
                                                    <a href="#"
                                                        onClick={(e) => { e.preventDefault(); window.history.back(); }}
                                                        disabled={isVerifying}
                                                    >
                                                        <i className="fa fa-arrow-left"></i> Use a different method
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </main>
                    </div>
                </div>
            </div>
        </>
    );
}

export default withRouter(SetupAuthenticator);

